import DOMPurify from 'dompurify';

/**
 * Sanitize HTML to prevent XSS attacks.
 * Allows safe tags (formatting, tables, images) but strips scripts and event handlers.
 * 
 * IMPORTANT: Images (img tags) are ALLOWED — including embedded email images,
 * screenshots, signatures, and inline photos. Do not block them.
 */
// Placeholder shown in place of a remote (http/https) image that has been blocked
// for privacy. Remote images in inbound email can act as tracking pixels that leak
// when/where a ticket was opened; we don't auto-fetch them until a user opts in.
const REMOTE_BLOCKED_PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='110' viewBox='0 0 200 110'%3E%3Crect fill='%23f3f4f6' stroke='%23d1d5db' stroke-dasharray='4 3' width='200' height='110'/%3E%3Ctext x='50%25' y='44%25' font-family='sans-serif' font-size='12' fill='%236b7280' text-anchor='middle'%3EExternal image hidden%3C/text%3E%3Ctext x='50%25' y='64%25' font-family='sans-serif' font-size='10' fill='%239ca3af' text-anchor='middle'%3E(protected for privacy)%3C/text%3E%3C/svg%3E";

// Normalize a URL value and decide whether it loads from the network.
// Catches http:/https:, protocol-relative //host, and mixed-case/whitespace/
// control-char obfuscation (e.g. "  HtTpS :" or "\t//evil.com"). cid: and data:
// are NOT remote (embedded/inline attachments stay allowed).
function _isRemoteUrl(raw) {
  if (!raw) return false;
  const v = String(raw).replace(/[\s\u0000-\u001f\u200b-\u200d\ufeff]+/g, '');
  if (!v || /^(data|blob|cid):/i.test(v)) return false;
  try {
    const parsed = new URL(v, 'https://inline.invalid/');
    return parsed.origin !== 'https://inline.invalid' || /^[a-z][a-z0-9+.-]*:/i.test(v);
  } catch {
    return true;
  }
}

// Remove any CSS declaration that loads a remote resource via url(), regardless of
// the property (background, background-image, list-style-image, border-image,
// content, cursor, etc.). Keeps non-resource styling (colors, spacing).
function _stripRemoteCssResources(style) {
  if (!style || style.indexOf('url(') === -1) return style;
  return style
    .split(';')
    .filter((decl) => {
      const m = decl.match(/url\(\s*['"]?([^'")]+)['"]?\s*\)/i);
      return !(m && _isRemoteUrl(m[1]));
    })
    .join(';');
}

// DOMPurify hook: rewrite remote image sources to the blocked placeholder and stash
// the original URL on a data-attr so it can be restored once the user opts in.
function _blockRemoteImagesHook(node) {
  if (!node || !node.getAttribute) return;
  if (node.tagName === 'IMG') {
    const src = node.getAttribute('src') || '';
    if (_isRemoteUrl(src)) {
      node.setAttribute('data-remote-src', src);
      node.setAttribute('data-remote-blocked', '1');
      node.setAttribute('src', REMOTE_BLOCKED_PLACEHOLDER);
    }
  }
  // Remove remote resource-loading attributes on any element.
  ['poster', 'background'].forEach((attr) => {
    if (_isRemoteUrl(node.getAttribute(attr) || '')) node.removeAttribute(attr);
  });
  // srcset is forbidden entirely (also in FORBID_ATTR) — strip defensively here too.
  if (node.hasAttribute && node.hasAttribute('srcset')) node.removeAttribute('srcset');
  // Strip remote resource-loading CSS from inline styles.
  const style = node.getAttribute('style');
  if (style) {
    const cleaned = _stripRemoteCssResources(style);
    if (cleaned !== style) node.setAttribute('style', cleaned);
  }
}

// True when any html fragment contains a remote (http/https/protocol-relative) image.
export function hasRemoteImages(htmls = []) {
  return (Array.isArray(htmls) ? htmls : [htmls]).some(
    (h) => typeof h === 'string' && /<img[^>]+src\s*=\s*["']?\s*(?:https?:|\/\/)/i.test(h)
  );
}

export function sanitizeHtml(dirty, { blockRemoteImages = false } = {}) {
  if (!dirty) return '';

  // Replace unresolved cid: image references BEFORE sanitizing.
  // DOMPurify strips the cid: scheme (not in ALLOWED_URI_REGEXP), which would leave a
  // src-less broken <img>. Swapping to a safe data-URI SVG placeholder first keeps the
  // image visible as "Embedded image" and avoids net::ERR_UNKNOWN_URL_SCHEME.
  const cidPlaceholder =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='90' viewBox='0 0 160 90'%3E%3Crect fill='%23f7f7f7' stroke='%23ddd' width='160' height='90'/%3E%3Ctext x='50%25' y='50%25' font-family='sans-serif' font-size='11' fill='%23999' text-anchor='middle' dominant-baseline='middle'%3EEmbedded image%3C/text%3E%3C/svg%3E";
  let pre = dirty
    .replace(/src\s*=\s*"cid:[^"]*"/gi, `src="${cidPlaceholder}"`)
    .replace(/src\s*=\s*'cid:[^']*'/gi, `src="${cidPlaceholder}"`);

  if (blockRemoteImages) DOMPurify.addHook('afterSanitizeAttributes', _blockRemoteImagesHook);
  const clean = DOMPurify.sanitize(pre, {
    ALLOWED_TAGS: [
      'p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'img', 'div', 'span',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'table', 'thead',
      'tbody', 'tr', 'td', 'th', 'hr', 'blockquote', 'pre', 'code', 'font',
      'sub', 'sup', 'strike', 's', 'del', 'ins', 'mark', 'center',
      'figure', 'figcaption', 'picture', 'source', 'video',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'style', 'target', 'rel',
      'width', 'height', 'colspan', 'rowspan', 'align', 'valign', 'color',
      'size', 'face', 'data-testid', 'border', 'cellpadding', 'cellspacing',
      'bgcolor', 'background',
    ],
    ALLOW_DATA_ATTR: true,
    // Allow all URI schemes for images (http, https, data, blob, mailto)
    ALLOWED_URI_REGEXP: /^(?:(?:https?|data|blob|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    // Do NOT strip images — critical for email screenshots and signatures
    FORBID_TAGS: [...(blockRemoteImages ? ['video', 'source'] : []), 'script', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'button'],
    FORBID_ATTR: [...(blockRemoteImages ? ['style', 'background', 'poster'] : []), 'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'srcset'],
  });

  if (blockRemoteImages) DOMPurify.removeHook('afterSanitizeAttributes');
  return clean;
}
